import { Box, Link as MuiLink, Paper, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import type { StockCountsDto } from '@app/shared';
import { api } from '../../lib/api';
import { Link } from 'react-router-dom';
import { useFeatureReleased } from '../../release';
import { useAuth } from '../auth/AuthProvider';

/**
 * The report links under Reporting (PC19), in the reference's order (walk of 2026-10-15). The
 * reference lists 40; a link is shown when 212Ez has the report and the user may open its page.
 * Each points at the screen that holds that report here (most are the batch report of a bulk tool).
 */
const LINKS: ReadonlyArray<{ label: string; to: string; code: string; feature?: string }> = [
  { label: 'Clear SIM Report (PC181)', to: '/inventory/bulk/clear-sim', code: 'PC351' },
  { label: 'Clear Device Report (PC180)', to: '/inventory/bulk/clear-device', code: 'PC354' },
  { label: 'Deactivate MDN Report (PC23)', to: '/inventory/bulk/deactivate-mdn', code: 'PC356' },
  { label: 'Swap SIM Batch Report (PC24)', to: '/inventory/bulk/swap-sim', code: 'PC358' },
  {
    label: 'SIM/UICCID Swap Report (PC656)',
    to: '/inventory/reporting/sim-swap',
    code: 'PC656',
    feature: 'reporting-3',
  },
  {
    label: 'Device Swap Report (PC503)',
    to: '/reports/swap-device',
    code: 'PC503',
    feature: 'reporting-2',
  },
  { label: 'Bulk Swap MDN Report (PC540)', to: '/inventory/bulk/swap-mdn', code: 'PC357' },
  {
    label: 'MDN Plan Change Report (PC184)',
    to: '/inventory/bulk/customer-plan-change',
    code: 'PC360',
    feature: 'reporting-3',
  },
  { label: 'MDN Topup Report (PC831)', to: '/inventory/bulk/mdn-topup', code: 'PC830' },
  {
    label: 'Nlad Status Check Report (PC849)',
    to: '/inventory/bulk/nlad-process',
    code: 'PC857',
    feature: 'reporting-2',
  },
  {
    label: 'Bulk Customer Plan Change Report (PC496)',
    to: '/inventory/bulk/customer-plan-change',
    code: 'PC360',
  },
  {
    label: 'Re-assign Inventory Report (PC310)',
    to: '/inventory/bulk/reassign-sim',
    code: 'PC352',
  },
  {
    label: 'Re-assign Device Report (PC311)',
    to: '/inventory/bulk/reassign-device',
    code: 'PC353',
  },
  {
    label: 'Manage Unassigned Inventory (PC306)',
    to: '/inventory/reporting/unassigned-inventory',
    code: 'PC306',
    feature: 'inventory-reports-2',
  },
  {
    label: 'Agent Inventory Report (PC307)',
    to: '/inventory/reporting/agent-inventory-report',
    code: 'PC307',
    feature: 'reporting-2',
  },
  {
    label: 'Agent Device Report (PC308)',
    to: '/inventory/reporting/agent-device-report',
    code: 'PC308',
    feature: 'reporting-2',
  },
  {
    label: 'Manage Unassigned Device (PC309)',
    to: '/inventory/reporting/unassigned-device',
    code: 'PC309',
    feature: 'inventory-reports-2',
  },
  {
    label: 'GSM Device Inventory for Agents (PC214)',
    to: '/inventory/reporting/agent-device-inventory',
    code: 'PC214',
    feature: 'inventory-reports-2',
  },
  {
    label: 'SIM Inventory for Agents (PC215)',
    to: '/inventory/reporting/agent-sim-inventory',
    code: 'PC215',
    feature: 'inventory-reports-2',
  },
  {
    label: 'Universal Inventory Reports (PC508)',
    to: '/inventory/reporting/universal-inventory',
    code: 'PC508',
    feature: 'reporting-3',
  },
  {
    label: 'Vendor Summary (PC183)',
    to: '/inventory/reporting/vendor-summary',
    code: 'PC183',
    feature: 'reporting-3',
  },
  {
    label: 'SIM Summary (PC182)',
    to: '/inventory/reporting/sim-summary',
    code: 'PC182',
    feature: 'inventory-reports-2',
  },
  {
    label: 'Inventory Update Batch Report (PC188)',
    to: '/inventory/bulk/update-sims',
    code: 'PC391',
  },
  {
    label: 'Inventory Update Device Batch Report (PC476)',
    to: '/inventory/bulk/update-devices',
    code: 'PC391',
  },
  { label: 'Inventory Report (PC20)', to: '/inventory/report', code: 'PC350' },
  {
    label: 'SIM Batch Report (PC187)',
    to: '/inventory/reporting/sim-batch',
    code: 'PC187',
    feature: 'reporting-3',
  },
  {
    label: 'Shipper Batch Report (PC186)',
    to: '/inventory/bulk/shipper-inventory',
    code: 'PC7',
    feature: 'shipper-inventory',
  },
  {
    label: 'Ship SIM to Agent (Store) By Creating Boxes (PC477)',
    to: '/inventory/reporting/sim-boxes',
    code: 'PC477',
    feature: 'reporting-3',
  },
  {
    label: 'Ship Device to Agent (Store) By Creating Boxes (PC478)',
    to: '/inventory/reporting/device-boxes',
    code: 'PC478',
    feature: 'reporting-3',
  },
  {
    label: 'Device Report (PC185)',
    to: '/inventory/reporting/device-batch',
    code: 'PC185',
    feature: 'reporting-3',
  },
  { label: 'De-enroll SIM Report (PC22)', to: '/inventory/bulk/deprovision-sim', code: 'PC355' },
  { label: 'Search SIM/Device (PC228)', to: '/inventory/search', code: 'PC345' },
  {
    label: 'Check SIM Compatibility (PC270)',
    to: '/inventory/reporting/check-sim-compatibility',
    code: 'PC270',
    feature: 'check-sim-compatibility',
  },
  {
    label: 'Unassigned Stock Report (PC644)',
    to: '/inventory/reporting/unassigned-stock',
    code: 'PC644',
    feature: 'inventory-reports-2',
  },
];

export function InventoryReportLinks() {
  const { can } = useAuth();
  const featureOn = useFeatureReleased();
  const shown = LINKS.filter((l) => can(l.code) && (!l.feature || featureOn(l.feature)));
  if (shown.length === 0) return null;
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, mt: 2 }}
      component="nav"
      aria-label="Inventory report links"
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Reports
      </Typography>
      <Box
        component="ul"
        sx={{
          m: 0,
          pl: 2.5,
          columns: { xs: 1, sm: 2, md: 3 },
          '& li': { breakInside: 'avoid', py: 0.25 },
        }}
      >
        {shown.map((l) => (
          <li key={l.label}>
            <MuiLink component={Link} to={l.to}>
              {l.label}
            </MuiLink>
          </li>
        ))}
      </Box>
    </Paper>
  );
}

const COUNTERS: ReadonlyArray<[key: keyof StockCountsDto, label: string]> = [
  ['simStock', 'SIM Stock'],
  ['simUsed', 'SIM Used'],
  ['simFree', 'SIM Free'],
  ['provisioned', 'Provisioned'],
  ['notProvisioned', 'Not Provisioned'],
  ['deviceStock', 'Device Stock'],
  ['deviceUsed', 'Device Used'],
  ['deviceFree', 'Device Free'],
];

/** The stock counters above Reporting's reports (switch `reporting-2`). */
export function StockCounters() {
  const counts = useQuery({
    queryKey: ['inventory-stock'],
    queryFn: () => api.get<StockCountsDto>('/inventory-stock'),
  });
  return (
    <Box
      component="section"
      aria-label="Stock counters"
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}
    >
      {COUNTERS.map(([key, label]) => (
        <Paper key={key} variant="outlined" sx={{ px: 1.5, py: 0.75, minWidth: 110 }}>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography sx={{ fontSize: 20, fontWeight: 600 }} aria-label={`${label} count`}>
            {counts.data ? counts.data[key] : '-'}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
}

/** Inventory Report (PC350)'s ten reports, in the reference's order (walk of 2026-10-27). */
const INVENTORY_REPORT_LINKS: ReadonlyArray<[label: string, key: string, code: string]> = [
  ['Agent Wise Inventory Report (SIM)', 'agent-wise-sim-inventory', 'PC487'],
  ['SIM Inventory By Agent', 'sim-inventory-by-agent', 'PC502'],
  ['SIM Inventory Detail', 'sim-inventory-detail', 'PC489'],
  ['Inventory Snapshot (SIM)', 'sim-inventory-snapshot', 'PC488'],
  ['Transaction Inventory Report (SIM)', 'sim-inventory-transactions', 'PC486'],
  ['Agent Device Inventory', 'agent-device-inventory-report', 'PC491'],
  ['Device By Agent', 'device-by-agent', 'PC501'],
  ['Device Inventory Detail', 'device-inventory-detail', 'PC493'],
  ['Device Inventory Snapshot', 'device-inventory-snapshot', 'PC492'],
  ['Transaction Device Inventory Report', 'device-inventory-transactions', 'PC490'],
];
export const INVENTORY_REPORT_KEYS = INVENTORY_REPORT_LINKS.map(([, key]) => key);

/** The links of Inventory Report; a link shows when the user has its code. */
export function InventoryReportList() {
  const { can } = useAuth();
  const shown = INVENTORY_REPORT_LINKS.filter(([, , code]) => can(code));
  if (shown.length === 0) return null;
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }} component="nav" aria-label="Inventory Report">
      <Box component="ul" sx={{ m: 0, pl: 2.5, columns: { xs: 1, sm: 2 }, '& li': { py: 0.25 } }}>
        {shown.map(([label, key, code]) => (
          <li key={key}>
            <MuiLink component={Link} to={`/inventory/report/${key}`}>
              {`${label} (${code})`}
            </MuiLink>
          </li>
        ))}
      </Box>
    </Paper>
  );
}
